import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';
import { catalystLabel } from '../utils/pendingSignals';

/**
 * The PM Scan Viewer: a stored day's triggers, and a way onto the chart.
 *
 * <h2>It READS, it does not compute</h2>
 *
 * Everything here comes from a file a batch wrote earlier. That is the point of the architecture —
 * many desks open the same day and none of them re-runs the ephemeris — and it is also why this
 * component has no "run" button: a viewer that could trigger a multi-minute universe scan from a
 * dropdown would do it by accident.
 *
 * <h2>A Table, not a DataGrid</h2>
 *
 * `@mui/x-data-grid` is not a dependency of this project, and adding one to render a few hundred
 * rows would be a new package, a new licence to read, and a second table idiom beside the several
 * plain `Table`s already here. Sorting and filtering are done in this component over the array,
 * which is all the grid would have given us at this size.
 *
 * <h2>The dates load once, when the dialog opens</h2>
 *
 * Not on mount — the dialog is built into the element tree long before anyone opens it, and a
 * component that fetched on mount would hit the API on every page load for a feature most sessions
 * never touch. `open` is the trigger.
 */
const DailyScanViewer = ({ open, onClose, onLoadSymbol }) => {
  const { api, token, isAdmin } = useAuth();

  const [dates, setDates] = useState([]);
  const [selected, setSelected] = useState('');
  const [scan, setScan] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingScan, setLoadingScan] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Two presses, not a window.confirm. A native confirm() blocks the whole page and cannot be
  // styled or tested; arming a button in place says the same thing and stays inside React.
  const [armed, setArmed] = useState(false);
  const [notice, setNotice] = useState('');

  /**
   * THE TOGGLES, as two independent multi-selects.
   *
   * <p>Direction and catalyst are different questions, so they are different controls. One
   * five-way group would make "Long" and "CTS" look like alternatives to each other, and there
   * would be no way to ask the only question a desk actually asks — "the CTS longs".
   *
   * <p>EMPTY MEANS ALL, in both groups. The alternative is to start with everything selected,
   * which reads identically on screen and behaves differently the moment a user deselects the
   * last chip: "show me nothing" is not a state anyone wants to reach by clicking one thing, and
   * it looks exactly like the scan having no rows.
   */
  const [directions, setDirections] = useState([]);
  const [catalysts, setCatalysts] = useState([]);

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadDates = useCallback(async () => {
    setLoadingDates(true);
    setError('');
    try {
      const res = await api.get('/api/integral-delta/scans', auth);
      const list = Array.isArray(res?.data?.dates) ? res.data.dates : [];
      setDates(list);
      // The newest is what a desk wants at 7am. Only auto-select when nothing is chosen, so a
      // refresh does not yank the user off the day they were reading.
      setSelected(prev => (prev && list.includes(prev) ? prev : (list[list.length - 1] || '')));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not list the stored scans.');
      setDates([]);
    } finally {
      setLoadingDates(false);
    }
  }, [api, auth]);

  useEffect(() => { if (open) loadDates(); }, [open, loadDates]);

  // Disarm on any change of date, and on close. An armed button is a promise about ONE file, and
  // carrying that arming across a dropdown change would delete a day nobody was looking at.
  //
  // It clears `armed` ONLY. Clearing the notice here too looked tidier and was wrong: a successful
  // delete sets the notice and clears the selection, so this effect fired immediately afterwards
  // and wiped the one line telling the operator what had happened. The notice is cleared where a
  // reader has actually moved on — when another scan loads, below.
  useEffect(() => { setArmed(false); }, [selected, open]);

  useEffect(() => {
    if (!open || !selected) { setScan(null); return; }
    let cancelled = false;
    setLoadingScan(true);
    setError('');
    setNotice('');          // the reader has moved on to another day
    api.get(`/api/integral-delta/scans/${selected}`, auth)
      .then((res) => { if (!cancelled) setScan(res?.data || null); })
      .catch((err) => {
        if (cancelled) return;
        setScan(null);
        setError(err?.response?.data?.error
          || `Could not load the scan for ${selected}.`);
      })
      // Guarded by `cancelled` because switching dates quickly can land two responses out of
      // order, and the slower one would otherwise overwrite the newer answer.
      .finally(() => { if (!cancelled) setLoadingScan(false); });
    return () => { cancelled = true; };
  }, [open, selected, api, auth]);

  /** The file is grouped by symbol; a table wants rows. Flattened once, not on every render. */
  const rows = useMemo(() => {
    const bySymbol = scan?.bySymbol;
    if (!bySymbol) return [];
    const out = [];
    Object.keys(bySymbol).forEach((symbol) => {
      (bySymbol[symbol] || []).forEach(s => out.push({ ...s, symbol: s.symbol || symbol }));
    });
    return out;
  }, [scan]);

  const visible = useMemo(() => {
    const q = filter.trim().toUpperCase();
    return rows.filter((r) => {
      if (directions.length && !directions.includes(r.direction)) return false;
      // Prefix, not equality: PTA covers PTA_CROSS and PTA_REVERSAL, which is what a reader
      // means by "PTA". Matching the whole name would need six chips to say one thing.
      if (catalysts.length
          && !catalysts.some(c => (r.catalyst || '').startsWith(c))) return false;
      if (!q) return true;
      return (r.symbol || '').toUpperCase().includes(q)
        || (r.catalyst || '').toUpperCase().includes(q)
        || (r.direction || '').toUpperCase().includes(q);
    });
  }, [rows, filter, directions, catalysts]);

  /** How many rows each chip would show on its own — so a zero chip reads as empty, not broken. */
  const counts = useMemo(() => {
    const c = { LONG: 0, SHORT: 0, CTS: 0, PTA: 0, PTB: 0 };
    rows.forEach((r) => {
      if (r.direction === 'LONG') c.LONG += 1;
      if (r.direction === 'SHORT') c.SHORT += 1;
      const cat = r.catalyst || '';
      if (cat.startsWith('CTS')) c.CTS += 1;
      if (cat.startsWith('PTA')) c.PTA += 1;
      if (cat.startsWith('PTB')) c.PTB += 1;
    });
    return c;
  }, [rows]);

  /**
   * Delete the stored file for the selected date.
   *
   * <p>Deletes ONLY. Regenerating is the existing Run Global Scan button, deliberately left as a
   * separate press: a delete that silently started a multi-minute universe scan would be a
   * surprise, and the two have very different costs.
   *
   * <p>The view is cleared on success — including the date's entry in the dropdown — because a
   * viewer still showing rows from a file that no longer exists is the most misleading state this
   * dialog can be in.
   */
  const handleDelete = useCallback(async () => {
    if (!selected) return;
    if (!armed) { setArmed(true); setNotice(''); return; }
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/api/integral-delta/scans/${selected}`, auth);
      const gone = selected;
      setScan(null);
      setSelected('');
      setDates(prev => prev.filter(d => d !== gone));
      setNotice(`Deleted the scan for ${gone}. Run Global Scan to regenerate it.`);
    } catch (err) {
      // A 404 means it was already gone, which is worth saying plainly rather than as a failure —
      // the operator's goal is met, and the list is refreshed so the phantom entry disappears.
      const status = err?.response?.status;
      setError(status === 404
        ? `There was no stored scan for ${selected} to delete.`
        : (err?.response?.data?.error || err?.message
          || `Could not delete the scan for ${selected}.`));
      // A 404 means the LIST is stale - it offered a date with no file behind it. Pruned here
      // rather than by re-listing, because loadDates() opens with setError(''), which would wipe
      // the message just set and leave the operator with a dialog that silently did nothing.
      if (status === 404) setDates(prev => prev.filter(d => d !== selected));
    } finally {
      setDeleting(false);
      setArmed(false);
    }
  }, [api, auth, selected, armed]);

  const handleLoad = useCallback((row) => {
    // The dialog closes first, then the chart loads. A modal still over the canvas while the chart
    // redraws behind it is the one thing the reader cannot see, and seeing it is the whole request.
    if (onClose) onClose();
    if (onLoadSymbol) {
      onLoadSymbol(row.symbol, rows.filter(r => r.symbol === row.symbol), selected);
    }
  }, [onClose, onLoadSymbol, rows, selected]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
            Daily Scan Viewer
          </Typography>

          <FormControl size="small" sx={{ minWidth: 190 }} disabled={loadingDates}>
            <InputLabel id="scan-date-label">Scan date</InputLabel>
            <Select
              labelId="scan-date-label"
              label="Scan date"
              value={dates.includes(selected) ? selected : ''}
              onChange={e => setSelected(e.target.value)}
            >
              {dates.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Filter"
            placeholder="symbol, catalyst…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            sx={{ width: 180 }}
          />

          <ToggleButtonGroup
            size="small"
            value={directions}
            onChange={(e, v) => setDirections(v)}
            aria-label="direction"
          >
            <ToggleButton value="LONG" aria-label="Long" color="success">
              Long&nbsp;<Typography variant="caption">({counts.LONG})</Typography>
            </ToggleButton>
            <ToggleButton value="SHORT" aria-label="Short" color="error">
              Short&nbsp;<Typography variant="caption">({counts.SHORT})</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            size="small"
            value={catalysts}
            onChange={(e, v) => setCatalysts(v)}
            aria-label="catalyst"
          >
            <ToggleButton value="CTS" aria-label="CTS">
              CTS&nbsp;<Typography variant="caption">({counts.CTS})</Typography>
            </ToggleButton>
            <ToggleButton value="PTA" aria-label="PTA">
              PTA&nbsp;<Typography variant="caption">({counts.PTA})</Typography>
            </ToggleButton>
            <ToggleButton value="PTB" aria-label="PTB">
              PTB&nbsp;<Typography variant="caption">({counts.PTB})</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          {(directions.length > 0 || catalysts.length > 0) && (
            <Button size="small" onClick={() => { setDirections([]); setCatalysts([]); }}>
              Clear filters
            </Button>
          )}

          {/* ADMIN ONLY. Deleting a stored scan removes a file every desk reads, so it is not a
              control an ordinary viewer should be able to reach by accident. The backend does not
              rely on this being hidden — the endpoint carries its own guard. */}
          {isAdmin && (
            <Tooltip
              arrow
              title={armed
                ? `Press again to delete the ${selected} scan permanently`
                : 'Delete this stored scan so it can be regenerated'}
            >
              <span>
                <Button
                  size="small"
                  color="error"
                  variant={armed ? 'contained' : 'outlined'}
                  startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
                  disabled={!selected || deleting || loadingScan}
                  onClick={handleDelete}
                >
                  {armed ? 'Confirm delete' : 'Delete scan'}
                </Button>
              </span>
            </Tooltip>
          )}

          <Tooltip arrow title="Re-read the list of stored scans">
            <span>
              <IconButton onClick={loadDates} disabled={loadingDates} size="small">
                {loadingDates ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

        {!loadingDates && dates.length === 0 && !error && !notice && (
          // An empty cupboard is not an error, and saying so is the difference between "wait for
          // tonight's batch" and "something is broken".
          <Alert severity="info">
            No scans have been stored yet. They appear here once a batch run has been filed.
          </Alert>
        )}

        {loadingScan && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loadingScan && scan && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {/* The provenance line. An empty scan is ambiguous without it: nothing fired, or
                  nothing was scanned, or the window was one bar wide. */}
              {visible.length === rows.length
                ? `${rows.length} trigger${rows.length === 1 ? '' : 's'}`
                : `${visible.length} of ${rows.length} triggers shown`}
              {' across '}{Object.keys(scan.bySymbol || {}).length} of {scan.universeSize} symbols
              {' · '}{scan.lookbackBars}-bar window
              {scan.generatedAt ? ` · generated ${scan.generatedAt}` : ''}
              {scan.failures?.length
                ? ` · ${scan.failures.length} symbol(s) failed to analyse` : ''}
            </Typography>

            {/* THE STALE BAR TRAP, made visible.
                A symbol whose feed has stopped still has bars, so the engine would keep
                re-reporting the trigger that fired on the day the data died — at the same price,
                looking exactly like a fresh signal. It is excluded instead, and a PM has to be
                told, because "excluded" and "quiet" are the same blank row otherwise. */}
            {scan.staleSymbols?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {scan.staleSymbols.length} symbol
                  {scan.staleSymbols.length === 1 ? '' : 's'} excluded — data older than{' '}
                  {scan.maxStaleDays} days
                </Typography>
                <Typography variant="caption" component="div">
                  These were NOT quiet. Their feeds have stopped, so any signal would be a stale
                  bar re-reported. Sorted by how long the data has been dead.
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {[...scan.staleSymbols]
                    .sort((a, b) => b.staleDays - a.staleDays)
                    .map(x => (
                      <Tooltip arrow key={x.symbol}
                        title={`last bar ${x.lastBarDate} — ${x.staleDays} days before this scan`}>
                        <Chip size="small" variant="outlined" color="warning"
                          label={`${x.symbol} · ${x.staleDays}d`} />
                      </Tooltip>
                    ))}
                </Box>
              </Alert>
            )}

            {scan.noDataSymbols?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {scan.noDataSymbols.length} symbol
                {scan.noDataSymbols.length === 1 ? ' has' : 's have'} no stored history at all:{' '}
                {scan.noDataSymbols.join(', ')}
              </Alert>
            )}

            {rows.length === 0 ? (
              <Alert severity="info">
                This scan ran and found no triggers. That is a result, not a missing file.
              </Alert>
            ) : (
              <TableContainer sx={{ maxHeight: '55vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Catalyst</TableCell>
                      <TableCell align="right">Trigger price</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Chart</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.map((r, i) => (
                      <TableRow hover key={`${r.symbol}-${r.date}-${r.catalyst}-${i}`}>
                        <TableCell sx={{ fontWeight: 600 }}>{r.symbol}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.direction}
                            color={r.direction === 'LONG' ? 'success' : 'error'}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip arrow title={r.detail || ''} placement="top-start">
                            <span>{catalystLabel(r.catalyst)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {Number.isFinite(r.triggerPrice) ? r.triggerPrice.toFixed(4) : '—'}
                        </TableCell>
                        <TableCell>{r.date}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ShowChartIcon />}
                            onClick={() => handleLoad(r)}
                            sx={{ textTransform: 'none' }}
                          >
                            Load Chart
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filter && (
              <Typography variant="caption" color="text.secondary">
                Showing {visible.length} of {rows.length} — the filter hides the rest.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

DailyScanViewer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  /**
   * Called with `(symbol, thatSymbolsSignals, scanDate)` when a row's Load Chart is pressed.
   * The signals travel with it so the chart can paint them without a second request for a file
   * this component has already read.
   */
  onLoadSymbol: PropTypes.func,
};

DailyScanViewer.defaultProps = { open: false };

export default DailyScanViewer;
